import { apiClient } from './client';

/**
 * Public configuration returned from the backend.
 * These are non-sensitive config values safe to use in the frontend.
 */
export interface PublicConfig {
  mapTiles: {
    maptilerApiKey: string | null;
  };
}

// Cache the config to avoid repeated API calls
let cachedConfig: PublicConfig | null = null;
let configPromise: Promise<PublicConfig> | null = null;

/**
 * Fetches public configuration from the backend.
 * The result is cached for the lifetime of the page.
 *
 * This is used for API keys that need to be used client-side
 * (like map tile providers) but should be stored server-side.
 */
export async function getPublicConfig(): Promise<PublicConfig> {
  // Return cached config if available
  if (cachedConfig) {
    return cachedConfig;
  }

  // If a request is already in flight, wait for it
  if (configPromise) {
    return configPromise;
  }

  // Fetch config from backend
  configPromise = apiClient
    .get<PublicConfig>('/config/public')
    .then((response) => {
      cachedConfig = response.data;
      return cachedConfig;
    })
    .catch((error) => {
      console.error('[ConfigApi] Failed to fetch public config:', error);
      // Return default empty config on error
      cachedConfig = {
        mapTiles: {
          maptilerApiKey: null,
        },
      };
      return cachedConfig;
    })
    .finally(() => {
      configPromise = null;
    });

  return configPromise;
}

/**
 * Gets the MapTiler API key from the backend config.
 * Returns null if the key is not configured.
 */
export async function getMapTilerApiKey(): Promise<string | null> {
  const config = await getPublicConfig();
  return config.mapTiles.maptilerApiKey;
}

/**
 * Clears the cached config (useful for testing or after config changes).
 */
export function clearConfigCache(): void {
  cachedConfig = null;
  configPromise = null;
}
